import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  TruckIcon, GlobeIcon, WarehouseIcon,
  SettingsIcon, CheckCircleIcon, ShieldIcon,
  HeartIcon, StarIcon,
} from '../components/Icons';
import './Home.css';

const Home: React.FC = () => {
  const [trackingId, setTrackingId] = useState('');

  return (
    <main className="home">
      {/* HERO */}
      <section className="hero">
        <div className="hero__overlay" />
        <img
          className="hero__bg"
          src="https://images.unsplash.com/photo-1586528116311-ad8dd3c8310d?w=1600&q=80"
          alt="Warehouse interior"
        />
        <div className="container hero__content">
          <p className="section-label hero__label">More than just delivery.</p>
          <h1 className="hero__title">One stop delivery<br />solution.</h1>
          <p className="hero__desc">
            Experience kinetic precision in logistics. From the heart of Accra to the edges of the globe, we move your business forward with unparalleled speed and absolute reliability.
          </p>
          <div className="hero__actions">
            <Link to="/contact" className="btn-primary">Schedule a Delivery</Link>
            <Link to="/contact" className="btn-outline">Track Shipment</Link>
          </div>
        </div>
      </section>

      {/* ABOUT */}
      <section className="about-section">
        <div className="container about-section__inner">
          <div className="about-section__text">
            <p className="section-label">Our Presence</p>
            <h2 className="section-title">Established in Accra,<br />Delivering to the World.</h2>
            <p className="about-section__desc">
              Based in the vibrant heart of East Legon, Accra, Droff Logistics has redefined the local courier landscape. We combine local cultural intelligence with international corporate standards to ensure that every package is handled with kinetic precision. Our mission is to provide a seamless bridge between commerce and customer.
            </p>
            <div className="about-section__values">
              <div className="about-value">
                <span className="about-value__icon"><HeartIcon size={22} strokeWidth={1.8} /></span>
                <span className="about-value__label">Passion</span>
                <span className="about-value__desc">Fueling every delivery with dedication.</span>
              </div>
              <div className="about-value">
                <span className="about-value__icon"><ShieldIcon size={22} strokeWidth={1.8} /></span>
                <span className="about-value__label">Integrity</span>
                <span className="about-value__desc">Transparency in every transaction.</span>
              </div>
              <div className="about-value">
                <span className="about-value__icon"><StarIcon size={22} strokeWidth={1.8} /></span>
                <span className="about-value__label">Excellence</span>
                <span className="about-value__desc">Setting the gold standard in logistics.</span>
              </div>
            </div>
            <p className="about-section__hq">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ display: 'inline', verticalAlign: 'middle', marginRight: 6, color: 'var(--red)' }}><path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" /><polyline points="9 22 9 12 15 12 15 22" /></svg>
              <strong>Headquarters:</strong> East Legon, Accra — Ghana
            </p>
          </div>
        </div>
      </section>

      {/* COMPREHENSIVE SOLUTIONS */}
      <section className="solutions">
        <div className="container">
          <div className="solutions__header">
            <h2 className="section-title">Comprehensive Solutions</h2>
            <p className="section-desc">
              We offer a versatile range of services designed to meet the demands of modern business dynamics.
            </p>
          </div>
          <div className="solutions__grid">
            <div className="solution-card">
              <div className="solution-card__icon"><TruckIcon size={28} strokeWidth={1.6} /></div>
              <h3 className="solution-card__title">On-Demand Delivery</h3>
              <p className="solution-card__desc">Lightning-fast same-day delivery service within the Accra metropolitan area. Reliable for urgent parcels.</p>
              <Link to="/services" className="solution-card__link">Learn More</Link>
            </div>
            <div className="solution-card">
              <div className="solution-card__icon"><GlobeIcon size={28} strokeWidth={1.6} /></div>
              <h3 className="solution-card__title">Nationwide &amp; Worldwide</h3>
              <p className="solution-card__desc">Connecting every corner of Ghana and expanding your reach globally through our extensive partner network.</p>
              <Link to="/services" className="solution-card__link">Learn More</Link>
            </div>
            <div className="solution-card">
              <div className="solution-card__icon"><WarehouseIcon size={28} strokeWidth={1.6} /></div>
              <h3 className="solution-card__title">Warehousing</h3>
              <p className="solution-card__desc">Secure, climate-controlled, and digitally tracked storage solutions for your inventory and high-value cargo.</p>
              <Link to="/services" className="solution-card__link">Learn More</Link>
            </div>
          </div>
        </div>
      </section>

      {/* FEATURES ROW */}
      <section className="features">
        <div className="container features__grid">
          <div className="feature-item">
            <div className="feature-item__icon"><SettingsIcon size={26} strokeWidth={1.6} /></div>
            <h3 className="feature-item__title">Customised Solutions</h3>
            <p className="feature-item__desc">Tailor-made logistics plans configured around your unique business requirements and budget.</p>
          </div>
          <div className="feature-item">
            <div className="feature-item__icon"><CheckCircleIcon size={26} strokeWidth={1.6} /></div>
            <h3 className="feature-item__title">Quality Service</h3>
            <p className="feature-item__desc">We hold each delivery to a standard and spec that a customer or recipient will never question.</p>
          </div>
          <div className="feature-item">
            <div className="feature-item__icon"><ShieldIcon size={26} strokeWidth={1.6} /></div>
            <h3 className="feature-item__title">Security Guarantee</h3>
            <p className="feature-item__desc">Your cargo is insured, monitored, and delivered in a tamper-evident seal with signature confirmation.</p>
          </div>
        </div>
      </section>

      {/* TRACK YOUR CARGO */}
      <section className="track">
        <div className="container track__inner">
          <div className="track__text">
            <p className="section-label" style={{ color: '#ed1d39' }}>Track &amp; Trace</p>
            <h2 className="track__title">Track Your Cargo</h2>
            <p className="track__desc">
              Enter your tracking ID below to get real-time updates on the status and location of your shipment.
            </p>
            <div className="track__form">
              <input
                type="text"
                placeholder="Enter tracking ID..."
                value={trackingId}
                onChange={e => setTrackingId(e.target.value)}
                className="track__input"
              />
              <button className="btn-primary track__btn">Track</button>
            </div>
          </div>
          <div className="track__map">
            <div className="track__map-placeholder">
              <svg viewBox="0 0 200 160" fill="none" xmlns="http://www.w3.org/2000/svg">
                <rect width="200" height="160" fill="#1a1a1a" />
                <rect x="20" y="40" width="160" height="80" rx="4" fill="#2a2a2a" />
                {[30,50,70,90,110,130,150].map(x => (
                  <line key={x} x1={x} y1="40" x2={x} y2="120" stroke="#333" strokeWidth="0.5" />
                ))}
                {[55,75,95].map(y => (
                  <line key={y} x1="20" y1={y} x2="180" y2={y} stroke="#333" strokeWidth="0.5" />
                ))}
                <circle cx="100" cy="80" r="8" fill="#ed1d39" opacity="0.2" />
                <circle cx="100" cy="80" r="4" fill="#ed1d39" />
                <line x1="100" y1="76" x2="100" y2="62" stroke="#ed1d39" strokeWidth="1.5" />
                <circle cx="100" cy="60" r="3" fill="none" stroke="#ed1d39" strokeWidth="1.5" />
              </svg>
            </div>
          </div>
        </div>
      </section>

      {/* CTA BANNER */}
      <section className="cta-banner">
        <div className="container cta-banner__inner">
          <h2 className="cta-banner__title">Ready to experience kinetic precision?</h2>
          <div className="cta-banner__actions">
            <Link to="/contact" className="btn-primary cta-banner__btn-white">Get a Quote</Link>
            <Link to="/contact" className="btn-outline cta-banner__btn">Contact Sales</Link>
          </div>
        </div>
      </section>
    </main>
  );
};

export default Home;
